import React, { Fragment, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { EditorialReviewEntities } from 'client/data/models/editorial-review';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { CORE_GALLERY_SIZE } from 'site-modules/core-page/utils/constants';
import { CATEGORIES_PLAIN } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { TopRatedRibbon } from 'site-modules/core-page/components/top-rated-ribbon/top-rated-ribbon';
import { withCoreMediaWrapper } from 'site-modules/shared/components/core-page/core-media-wrapper/core-media-wrapper';
import { buildAllPhotosPath, buildSpinPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { getPhotoflipperProps } from 'site-modules/core-page/utils/photo-gallery';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import { PhotoGalleryPhoto } from './photo-gallery-photo';
import { PhotoGalleryRow } from './photo-gallery-row';

import './photo-gallery.scss';

function PhotoGalleryUI({
  params,
  vehicle,
  editorialReview = null,
  interiorPhotos = null,
  children = null,
  isPreprod = false,
  isUsed = false,
  isMobile = false,
  coreMedia = null,
  isConnect = false,
  hasSpinPhotos = false,
  hideGalleryRow = false,
  hideHeroPhoto = false,
  isCore6981Test = false,
  coreEtrActivationRecipe,
}) {
  const isTrackedPhotoProvider = useRef(false);

  const trackPhotosProvider = useCallback(
    rowPhotos => {
      if (isTrackedPhotoProvider.current || !coreMedia || (!hideGalleryRow && !rowPhotos)) {
        return;
      }

      isTrackedPhotoProvider.current = true;

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_name: TrackingConstant.ACTION_LIST,
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          creative_id: 'photoflipper',
          photo_type: [coreMedia.heroImage]
            .concat(rowPhotos)
            .filter(v => v)
            .map(photo => get(photo, 'provider', ''))
            .join(),
        },
      });
    },
    [coreMedia, hideGalleryRow]
  );

  useEffect(trackPhotosProvider);

  if (!coreMedia) {
    return null;
  }

  const { heroImage, allMedia, totalCount } = coreMedia;
  const photoflipperParams = { ...params, submodel: vehicle.submodels.name };
  const photoflipper360Props = getPhotoflipperProps({
    photoNum: 1,
    params: photoflipperParams,
    altMedia: allMedia,
    isPreprod,
    is360Tab: true,
    isCore6981Test,
  });

  return (
    <div className="photo-gallery pos-r mb-lg-0_25">
      {!hideHeroPhoto && (
        <div className="outer-wrapper pos-r">
          {!!children && (
            <div className="overlay-content pos-a top-0 left-0 w-100 px-1 px-md-1_5 pb-1 pb-md-1_5 pt-1_5 text-center text-blue-100">
              {children}
            </div>
          )}
          {!!heroImage && (
            <PhotoGalleryPhoto
              photoData={heroImage}
              params={photoflipperParams}
              isPreprod={isPreprod}
              media={allMedia}
              isHeroPhoto
              totalCount={totalCount}
              isMobile={isMobile}
              isCore6981Test={isCore6981Test}
            />
          )}
          {!isUsed && (
            <TopRatedRibbon
              vehicleParams={{
                makeSlug: params.make,
                modelSlug: params.model,
                year: params.year,
                submodelSlug: vehicle.submodels.slug,
              }}
              isMobile={isMobile}
              coreEtrActivationRecipe={coreEtrActivationRecipe}
            />
          )}
          {!isConnect && !hasSpinPhotos && isMobile && (
            <i
              className="icon-images text-white rounded-8 p-0_75 mt-0_5 mb-0_5 me-0_5"
              style={{ position: 'absolute', top: 0, right: 0 }}
              aria-hidden
            />
          )}

          {hasSpinPhotos && (
            <PhotoflipperButtonAsync tag={Fragment} {...photoflipper360Props}>
              <button
                className="icon-360-container border-0 rounded-8 mt-0_5 me-0_5 p-1"
                data-tracking-id="open_photoflipper_360"
                data-tracking-parent="360-indication-top"
                style={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}
              >
                <span className="visually-hidden">360° vehicle view</span>
                <img src={getStaticImageUrl('/icons/360.svg')} alt="" width={isMobile ? 50 : undefined} />
              </button>
            </PhotoflipperButtonAsync>
          )}
        </div>
      )}
      {!hideGalleryRow && (
        <PhotoGalleryRow
          interiorPhotos={interiorPhotos}
          editorialReview={editorialReview}
          params={photoflipperParams}
          vehicle={vehicle}
          isPreprod={isPreprod}
          photoSize={CORE_GALLERY_SIZE.DESKTOP.ROW}
          trackPhotosProvider={trackPhotosProvider}
          isCore6981Test={isCore6981Test}
          isMobile={isMobile}
        />
      )}
    </div>
  );
}

PhotoGalleryUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  editorialReview: EditorialReviewEntities.EditorialReview,
  coreMedia: PropTypes.shape({
    heroImage: PropTypes.shape({
      num: PropTypes.number,
      title: PropTypes.string,
      src: PropTypes.string,
      isLifestylePhoto: PropTypes.bool,
    }),
    allMedia: PropTypes.shape({
      photos: PropTypes.arrayOf(PropTypes.shape({})),
      videos: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    totalCount: PropTypes.number,
  }),
  interiorPhotos: PropTypes.arrayOf(PropTypes.shape({})),
  children: PropTypes.node,
  isPreprod: PropTypes.bool,
  isUsed: PropTypes.bool,
  isMobile: PropTypes.bool,
  isConnect: PropTypes.bool,
  hasSpinPhotos: PropTypes.bool,
  hideHeroPhoto: PropTypes.bool,
  hideGalleryRow: PropTypes.bool,
  isCore6981Test: PropTypes.bool,
  coreEtrActivationRecipe: PropTypes.string,
};

export const PhotoGallery = connectToModel(withCoreMediaWrapper(PhotoGalleryUI), {
  hasSpinPhotos: bindToPath(
    ({
      params: { make, model, year },
      vehicle: {
        submodels: { name: submodel },
      },
    }) => buildSpinPhotosPath({ make, model, year, submodel }),
    PhotoflipperModel,
    spins => !!get(spins, 'length')
  ),
  interiorPhotos: bindToPath(
    ({ params, vehicle, hideGalleryRow, isMobile }) =>
      !hideGalleryRow &&
      buildAllPhotosPath({
        ...params,
        pageNum: 1,
        submodel: vehicle.submodels.name,
        category: CATEGORIES_PLAIN.INTERIOR,
        imageSize: isMobile ? CORE_GALLERY_SIZE.MOBILE.ROW.width : CORE_GALLERY_SIZE.DESKTOP.ROW.width,
      }),
    PhotoflipperModel,
    photos => photos?.data?.map((photo, index) => ({ ...photo, num: index + 1 }))
  ),
});
